// HR > Master Task Sheet (Phase 3). A reusable duty library + per-employee duty
// assignment sheet (grouped Daily/Weekly/Monthly/Yearly, printable).
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { HRC, Card, SectionTitle, SubTabs, Th, Td, Table, Btn, Stat, Field, input, Modal, Empty, Loading } from './hrkit';
import { reportError } from '../../utils/reportError';

const FREQS = [
  { key: 'daily', letter: 'D', label: 'Daily', color: HRC.navy },
  { key: 'weekly', letter: 'W', label: 'Weekly', color: '#8B5CF6' },
  { key: 'monthly', letter: 'M', label: 'Monthly', color: HRC.green },
  { key: 'yearly', letter: 'Y', label: 'Yearly', color: HRC.amber },
];
const freqMeta = (k) => FREQS.find(f => f.key === k) || { letter: '?', label: k, color: HRC.muted };

function FreqTag({ f, full }) {
  const m = freqMeta(f);
  return <span style={{ display: 'inline-block', minWidth: full ? 'auto' : 20, textAlign: 'center', padding: full ? '2px 9px' : '2px 6px',
    borderRadius: 6, fontSize: 11, fontWeight: 800, color: m.color, background: m.color + '1A' }}>{full ? m.label : m.letter}</span>;
}

export default function MasterTaskSheet() {
  const [view, setView] = useState('library');
  return (
    <div>
      <SubTabs tabs={[{ key: 'library', label: '📚 Duty Library' }, { key: 'sheet', label: '🧑 Employee Sheet' }]} active={view} onChange={setView} />
      {view === 'library' ? <DutyLibrary /> : <EmployeeSheet />}
    </div>
  );
}

/* ═══════════════ Duty Library ═══════════════ */
function DutyLibrary() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [freq, setFreq] = useState('');
  const [q, setQ] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {}; if (freq) params.frequency = freq;
    api.get('/hr/duty-library', { params }).then(r => setRows(r.data.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [freq]);
  useEffect(() => { load(); }, [load]);

  const toggleActive = (d) => api.patch(`/hr/duty-library/${d.id}`, { is_active: !d.is_active }).then(load).catch(reportError('change this duty'));
  const counts = FREQS.map(f => ({ ...f, n: rows.filter(r => r.frequency === f.key).length }));

  // A TEXT SEARCH, NOT AN OWNER FILTER — and the label says so on purpose.
  //
  // Every one of the 237 library rows currently has a PERSON'S NAME in `particulars` and the
  // duty text in `default_due`, which is the reverse of what migration 065 intended
  // (particulars = the duty; default_due = a short hint like "EOD"). The employee link lives
  // in hr_employee_duties, which has 0 rows — that half of the design has never been used.
  //
  // So there is no owner column to filter on. This searches the raw text of both fields,
  // which happens to find a person today only because the data is shaped wrongly. Labelling
  // it "filter by owner" would make the screen assert a relationship the database does not
  // hold, and would quietly harden the defect. When the rows are migrated onto
  // hr_employee_duties this box should be REPLACED by a real owner filter reading the FK.
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? rows.filter(r => `${r.particulars || ''} ${r.default_due || ''}`.toLowerCase().includes(needle))
    : rows;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, marginBottom: 16 }}>
        <Stat label="Duties" value={rows.length} />
        {counts.map(c => <Stat key={c.key} label={c.label} value={c.n} accent={c.color} />)}
      </div>
      <Card>
        <SectionTitle right={<Btn onClick={() => { setEdit(null); setShowForm(true); }}>+ Add Duty</Btn>}>Master Duty Library</SectionTitle>
        <div style={{ display: 'flex', gap: 10, marginBottom: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={freq} onChange={e => setFreq(e.target.value)} style={{ ...input, width: 'auto', minWidth: 140 }}>
            <option value="">All frequencies</option>
            {FREQS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
          <input value={q} onChange={e => setQ(e.target.value)} style={{ ...input, width: 'auto', minWidth: 260, flex: 1 }}
            placeholder="Search text — matches Particulars and Default Due" />
          {q && <Btn size="sm" variant="soft" onClick={() => setQ('')}>Clear</Btn>}
        </div>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 14 }}>
          Plain text search, not an owner filter — this library has no employee column yet.
          {needle && <> Showing <b>{shown.length}</b> of {rows.length}{shown.length < rows.length && <> · {rows.length - shown.length} hidden</>}.</>}
        </div>
        {loading ? <Loading /> : rows.length === 0 ? <Empty>No master duties yet.</Empty>
          : shown.length === 0 ? <Empty>Nothing matches “{q}”.</Empty> : (
          <Table>
            <thead><tr><Th>Particulars</Th><Th>Frequency</Th><Th>Default Due</Th><Th>Active</Th><Th style={{ textAlign: 'right' }}></Th></tr></thead>
            <tbody>
              {shown.map(d => (
                <tr key={d.id} style={d.is_active ? undefined : { opacity: 0.5 }}>
                  <Td style={{ fontWeight: 600 }}>{d.particulars}</Td>
                  <Td><FreqTag f={d.frequency} full /></Td>
                  <Td>{d.default_due || '—'}</Td>
                  <Td><input type="checkbox" checked={d.is_active} onChange={() => toggleActive(d)} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: HRC.green }} /></Td>
                  <Td style={{ textAlign: 'right' }}><Btn size="sm" variant="soft" onClick={() => { setEdit(d); setShowForm(true); }}>Edit</Btn></Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      {showForm && <DutyForm duty={edit} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function DutyForm({ duty, onClose, onSaved }) {
  const [f, setF] = useState({ particulars: duty?.particulars || '', frequency: duty?.frequency || 'daily', default_due: duty?.default_due || '' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  const submit = () => {
    if (!f.particulars.trim()) { setErr('Particulars required'); return; }
    setSaving(true); setErr('');
    const req = duty ? api.patch(`/hr/duty-library/${duty.id}`, f) : api.post('/hr/duty-library', f);
    req.then(onSaved).catch(e => setErr(e.response?.data?.error || 'Failed')).finally(() => setSaving(false));
  };
  return (
    <Modal title={duty ? 'Edit Duty' : 'Add Duty'} onClose={onClose} width={520}>
      <div style={{ display: 'grid', gap: 12 }}>
        <Field label="Particulars *"><input style={input} value={f.particulars} onChange={set('particulars')} /></Field>
        <Field label="Frequency">
          <select style={input} value={f.frequency} onChange={set('frequency')}>{FREQS.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}</select>
        </Field>
        <Field label="Default Due (hint)"><input style={input} value={f.default_due} onChange={set('default_due')} placeholder="e.g. EOD, Every Mon, 1st" /></Field>
      </div>
      {err && <div style={{ color: HRC.red, fontSize: 12, marginTop: 10 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={saving}>{saving ? 'Saving…' : duty ? 'Save' : 'Add Duty'}</Btn>
      </div>
    </Modal>
  );
}

/* ═══════════════ Per-Employee Sheet ═══════════════ */
function EmployeeSheet() {
  const [emps, setEmps] = useState([]);
  const [empId, setEmpId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAssign, setShowAssign] = useState(false);

  useEffect(() => { api.get('/hr/employees', { params: { status: 'active' } }).then(r => setEmps(r.data.data || [])).catch(() => {}); }, []);

  const load = useCallback(() => {
    if (!empId) { setData(null); return; }
    setLoading(true);
    api.get(`/hr/employees/${empId}/duties`).then(r => setData(r.data.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [empId]);
  useEffect(() => { load(); }, [load]);

  const duties = data?.duties || [];
  const emp = data?.employee;

  const reorder = (id, dir) => {
    const idx = duties.findIndex(d => d.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= duties.length) return;
    if (duties[swap].frequency !== duties[idx].frequency) return;   // reorder within the same frequency group
    const arr = duties.slice();
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    api.post(`/hr/employees/${empId}/duties/reorder`, { ordered_ids: arr.map(d => d.id) }).then(load).catch(reportError('reorder the duties'));
  };
  const remove = (id) => api.delete(`/hr/employee-duties/${id}`).then(load).catch(reportError('remove this duty'));
  const saveDue = (d, val) => { if (val !== (d.due_date || '')) api.patch(`/hr/employee-duties/${d.id}`, { due_date: val }).then(load).catch(reportError('save the due date')); };

  let sl = 0;
  return (
    <div>
      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: HRC.muted }}>Employee</span>
          <select value={empId} onChange={e => setEmpId(e.target.value)} style={{ ...input, width: 'auto', minWidth: 260 }}>
            <option value="">— Select an employee —</option>
            {emps.map(e => <option key={e.id} value={e.id}>{e.employee_code ? `${e.employee_code} · ` : ''}{e.full_name}</option>)}
          </select>
          {emp && <>
            <Btn onClick={() => setShowAssign(true)}>+ Assign Duties</Btn>
            <Btn variant="ghost" onClick={() => window.print()}>🖨 Print</Btn>
          </>}
        </div>
      </Card>

      {!empId ? <Empty>Pick an employee to view their Master Task Sheet.</Empty> : loading ? <Loading /> : !emp ? <Empty>Could not load.</Empty> : (
        <Card>
          {/* Printable header */}
          <div style={{ borderBottom: `2px solid ${HRC.violet}`, paddingBottom: 12, marginBottom: 14 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: HRC.text }}>Master Task Sheet</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 8, marginTop: 8, fontSize: 12 }}>
              <HeaderField label="Name" value={`${emp.full_name}${emp.employee_code ? ` (${emp.employee_code})` : ''}`} />
              <HeaderField label="Department" value={[emp.department_name, emp.designation].filter(Boolean).join(' · ')} />
              <HeaderField label="Location" value={emp.address || emp.company_name} />
              <HeaderField label="Email" value={emp.email} />
              <HeaderField label="Contact" value={emp.phone} />
            </div>
          </div>

          {duties.length === 0 ? <Empty>No duties assigned yet — use “Assign Duties”.</Empty> : (
            <Table>
              <thead>
                <tr>
                  <Th style={{ width: 50 }}>Sl.No</Th><Th style={{ width: 90 }}>Frequency</Th>
                  <Th style={{ width: 160 }}>Due Date</Th><Th>Particulars</Th><Th style={{ width: 120, textAlign: 'right' }}></Th>
                </tr>
              </thead>
              <tbody>
                {FREQS.map(fr => {
                  const group = duties.filter(d => d.frequency === fr.key);
                  if (!group.length) return null;
                  return (
                    <React.Fragment key={fr.key}>
                      <tr><Td colSpan={5} style={{ background: HRC.pageBg, fontWeight: 800, color: fr.color, fontSize: 12 }}>{fr.label}</Td></tr>
                      {group.map(d => {
                        sl += 1;
                        return (
                          <tr key={d.id}>
                            <Td>{sl}</Td>
                            <Td><FreqTag f={d.frequency} /></Td>
                            <Td><input defaultValue={d.due_date || ''} placeholder={d.default_due || 'due…'} onBlur={e => saveDue(d, e.target.value)} style={{ ...input, padding: '4px 8px', fontSize: 12 }} /></Td>
                            <Td style={{ fontWeight: 600 }}>{d.particulars}</Td>
                            <Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <button onClick={() => reorder(d.id, -1)} title="Up" style={arrowBtn}>↑</button>
                              <button onClick={() => reorder(d.id, 1)} title="Down" style={arrowBtn}>↓</button>
                              <Btn size="sm" variant="ghost" onClick={() => remove(d.id)} style={{ marginLeft: 6 }}>Remove</Btn>
                            </Td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}

          <div style={{ marginTop: 14, fontSize: 11, color: HRC.muted }}>
            Legend: {FREQS.map(f => <span key={f.key} style={{ marginRight: 12 }}><strong style={{ color: f.color }}>{f.letter}</strong> = {f.label}</span>)}
          </div>
        </Card>
      )}

      {showAssign && <AssignModal empId={empId} assigned={duties.map(d => d.duty_id)} onClose={() => setShowAssign(false)} onSaved={() => { setShowAssign(false); load(); }} />}
    </div>
  );
}

function HeaderField({ label, value }) {
  return <div><span style={{ color: HRC.muted, fontWeight: 700 }}>{label}: </span><span style={{ color: HRC.text }}>{value || '—'}</span></div>;
}
const arrowBtn = { border: `1px solid ${HRC.border}`, background: '#fff', borderRadius: 6, cursor: 'pointer', width: 24, height: 24, marginLeft: 2, fontSize: 12, color: HRC.navy };

function AssignModal({ empId, assigned, onClose, onSaved }) {
  const [lib, setLib] = useState([]);
  const [sel, setSel] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/hr/duty-library', { params: { active: true } }).then(r => setLib(r.data.data || [])).catch(() => {}); }, []);
  const toggle = (id) => setSel(s => ({ ...s, [id]: !s[id] }));
  const ids = Object.keys(sel).filter(k => sel[k]);
  const submit = () => {
    if (!ids.length) return;
    setSaving(true);
    api.post(`/hr/employees/${empId}/duties`, { duty_ids: ids }).then(onSaved).catch(() => setSaving(false));
  };
  return (
    <Modal title="Assign Duties" onClose={onClose} width={560}>
      <div style={{ fontSize: 12, color: HRC.muted, marginBottom: 10 }}>Already-assigned duties are pre-checked and skipped (no duplicates).</div>
      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {lib.length === 0 ? <Empty>No active library duties.</Empty> : lib.map(d => {
          const isAssigned = assigned.includes(d.id);
          return (
            <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #F4F3F0', opacity: isAssigned ? 0.55 : 1 }}>
              <input type="checkbox" disabled={isAssigned} checked={isAssigned || !!sel[d.id]} onChange={() => toggle(d.id)} style={{ width: 16, height: 16, accentColor: HRC.navy }} />
              <FreqTag f={d.frequency} />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{d.particulars}</span>
              {isAssigned && <span style={{ fontSize: 10, color: HRC.green, fontWeight: 700 }}>assigned</span>}
            </label>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={saving || !ids.length}>{saving ? 'Assigning…' : `Assign ${ids.length || ''}`.trim()}</Btn>
      </div>
    </Modal>
  );
}
